import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getQueryWorker } from '@/engine/worker-client';
import { useDatasetStore } from '@/store/dataset-store';
import { useStorageStore } from '@/store/storage-store';
import { configDb } from '@/persistence/config-db';
import type { DatasetPreview } from '@/types/dataset';

const ACCEPTED_EXTENSIONS = ['.csv', '.xlsx', '.xls'];

function isAcceptedFile(file: File): boolean {
  const name = file.name.toLowerCase();
  return ACCEPTED_EXTENSIONS.some((ext) => name.endsWith(ext));
}

/**
 * Result of {@link useUploadHandler}. The hook owns the lifecycle
 * shared between {@link UploadButton} and {@link UploadDropZone}; the
 * caller still owns its input ref and drag-over state.
 */
export type UploadHandlerResult = {
  /** True while a file is being parsed by the worker. */
  busy: boolean;
  /** True when storage usage is past the block threshold (D-027). */
  blocked: boolean;
  /**
   * Handle a single user-supplied file end-to-end: extension check,
   * storage-block check, worker load + addDataset, dashboardName
   * rehydration, background profile, error reporting, and storage
   * refresh on completion. Returns the loaded preview, or null when
   * the upload was skipped (no file / wrong extension / blocked) or
   * failed (error already surfaced through `dataset-store.setStatus`).
   *
   * Callers wire input.value resets and drag-over cleanup themselves.
   */
  handleFile: (file: File | null | undefined) => Promise<DatasetPreview | null>;
};

/**
 * Shared upload lifecycle for {@link UploadButton} and
 * {@link UploadDropZone}. Before R-7 (audit revisions) the two
 * components carried byte-for-byte identical `handleFiles` bodies —
 * any change to the upload sequence needed to land twice.
 */
export function useUploadHandler(): UploadHandlerResult {
  const { t } = useTranslation();
  const setStatus = useDatasetStore((s) => s.setStatus);
  const addDataset = useDatasetStore((s) => s.addDataset);
  const setDatasetProfile = useDatasetStore((s) => s.setDatasetProfile);
  const setDashboardName = useDatasetStore((s) => s.setDashboardName);
  const storageStatus = useStorageStore((s) => s.estimate.status);
  const refreshStorage = useStorageStore((s) => s.refresh);

  const [busy, setBusy] = useState(false);
  const blocked = storageStatus === 'block';

  async function handleFile(
    file: File | null | undefined,
  ): Promise<DatasetPreview | null> {
    if (!file) return null;

    if (!isAcceptedFile(file)) {
      setStatus({
        kind: 'error',
        message: t('upload.unsupportedFile', { fileName: file.name }),
      });
      return null;
    }

    if (blocked) {
      setStatus({ kind: 'error', message: t('upload.blockedError') });
      return null;
    }

    setBusy(true);
    setStatus({ kind: 'loading', fileName: file.name });

    try {
      const api = getQueryWorker();
      const preview = await api.loadFile(file);
      addDataset(preview, file.name);

      // Rehydrate any previously-saved dashboard name for this dataset
      // id — datasetIds are deterministic from the file name (Phase 1)
      // so a re-upload of the same file restores the user's label.
      configDb.preferences
        .get(`dashboardName:${preview.datasetId}`)
        .then((pref) => {
          if (pref?.value) setDashboardName(preview.datasetId, pref.value);
        })
        .catch((err: unknown) => {
          console.warn('dashboardName load failed', err);
        });

      // Profile runs in the background — UI stays responsive while it
      // populates. Failure is non-fatal (UI falls back to raw types).
      api
        .profileDataset(preview.datasetId)
        .then((profile) => setDatasetProfile(preview.datasetId, profile))
        .catch((err: unknown) => {
          console.warn('profileDataset failed', err);
        });

      return preview;
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      setStatus({ kind: 'error', message });
      return null;
    } finally {
      setBusy(false);
      void refreshStorage();
    }
  }

  return { busy, blocked, handleFile };
}
